import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { isMobile } from 'utils/is-mobile';
import { TMarketType, TMarketTypeResponse } from 'interfaces/sportsbook';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { swarmCommand } from 'services/get-swarm-data';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setMarketType, setMarketTypes } from 'store/actions';
import { getMarketType, getMarketTypes } from 'store/selectors/sport-data';

type TResult = {
  marketTypes: TMarketType[] | null;
  currentMarketType: TMarketType | null;
  callMarketTypes: () => void;
};

export const useMarketTypes = (): TResult => {
  const dispatch = useDispatch();
  const configs = useSportsbookConfigs();

  const marketTypes = useSelector(getMarketTypes);
  const currentMarketType = useSelector(getMarketType);

  const { params } = useSbRouteMatch();

  const callMarketTypes = useCallback(() => {
    if (marketTypes[params.sport]) {
      return;
    }

    if (
      !isMobile() &&
      configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN
    ) {
      dispatch(setMarketTypes({ ...marketTypes, [params.sport]: null }));
      dispatch(setMarketType({ ...currentMarketType, [params.sport]: null }));

      return;
    }

    const relations = { sport_alias: params.sport };

    swarmCommand(
      CommandNames.GET_MARKET_TYPE,
      relations,
      (data: TMarketTypeResponse) => {
        if (!data.details && params.sport) {
          return;
        }

        if (!data.details.length) {
          dispatch(setMarketTypes({ ...marketTypes, [params.sport]: null }));
          dispatch(
            setMarketType({ ...currentMarketType, [params.sport]: null })
          );

          return;
        }

        const markets = data.details
          .sort((a, b) => a.Order - b.Order)
          .map(({ MarketTypeName, BasaltKind }) => ({
            label: MarketTypeName.replace(/\{sw\}|\{pw\}|\{s\}/g, ''),
            value: BasaltKind
          }));

        dispatch(setMarketTypes({ ...marketTypes, [params.sport]: markets }));
        dispatch(
          setMarketType({ ...currentMarketType, [params.sport]: markets[0] })
        );
      }
    );
  }, [params.sport, marketTypes, currentMarketType]);

  return useMemo(() => {
    const getter = SpringConfigs.MOCKED_DATA ? 'Soccer' : params.sport;

    return {
      marketTypes: marketTypes[getter],
      currentMarketType: currentMarketType[getter],
      callMarketTypes
    };
  }, [params.sport, marketTypes, currentMarketType, callMarketTypes]);
};
